import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useListParams } from "hooks/useListParams";
import {
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import { useVehicleStock } from "hooks/stockHook";
import classnames from "classnames";
import ImageGallery from "./ImageGallery";
import QuickActions from "./QuickActions";
import Pricing from "./Pricing";
import AdditionalInformation from "./AdditionalInformation";
import DetailsTab from "./DetailsTab";
import MoneyReceiptsTab from "./MoneyReceiptsTab";
import PreOrdersTab from "./PreOrdersTab";
import VehicleMovementList from "pages/VehicleMovementPages/VehicleMovementList/VehicleMovementList";
import QuotationsTab from "./QuotationsTab";
import StockHeader from "./StockHeader";
import DocumentGenerationModal from "./DocumentGenerationModal";
import { PLACEHOLDERS } from "assets/images";
import ShareVehicleImages from "components/Common/ShareVehicleImages";
import ImageZipDownloader from "./ImageZipDownloader";
import InventoryTab from "./InventoryTab";
import ActivityTab from "./ActivityTab";
import VehicleContentsTab from "./VehicleContentsTab";
import { useAuth } from "hooks/authHook";
import { ACCESS } from "constants/accessPermissions";

const StockView = () => {
  const { stockId } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isFetching, refetch } = useVehicleStock(stockId)
  const stock = data?.data || {}
  const { hasAnyPermission } = useAuth()
  const canViewActivity = hasAnyPermission([ACCESS.ACTIVITY_LOG.VIEW_LIST])
  const canViewContents = hasAnyPermission([ACCESS.VEHICLE_CONTENT.VIEW_VEHICLE_CONTENT_LIST])
  const { params, updateParams } = useListParams({ tab: "details" })
  const [activeTab, setActiveTab] = useState(params?.tab || "details")
  const [docType, setDocType] = useState("")
  const [inventoryPage, setInventoryPage] = useState(1)

  document.title = `Vehicle Stock${
    stock?.brand
      ? ` | ${stock?.brand.name} ${stock?.model?.name} ${stock?.stock_id}`
      : ""
  }`

  const toggleTab = tab => {
    updateParams({ tab })
    if (activeTab !== tab) setActiveTab(tab)
  }

  useEffect(() => {
    if (params?.tab && params.tab !== activeTab) setActiveTab(params.tab)
  }, [params?.tab])

  const carImages = stock?.images?.length
    ? stock?.images?.map(item => item?.url)
    : [PLACEHOLDERS.comingSoon]

  const addNewStock = () => {
    navigate(`/stock-add-edit`)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Vehicle Stock"
          breadcrumbItem={"Details"}
          loading={isLoading}
        />
        {/* Header */}
        <StockHeader
          stock={stock}
          loading={isLoading}
          onAddNewStock={addNewStock}
        />

        <Row>
          {/* Left Column */}
          <Col lg="4" className="mb-4">
            <ImageGallery
              stock={stock}
              carImages={carImages}
              loading={isLoading}
            />
            <ShareVehicleImages vehicleId={stock?.id} />

            <ImageZipDownloader vehicleId={stock?.stock_id} />
            <QuickActions setDocType={setDocType} />
            <Pricing stock={stock} loading={isLoading} />
            <AdditionalInformation stock={stock} loading={isLoading} />
          </Col>

          {/* Right Column */}
          <Col lg="8">
            <Nav pills className="nav-pills-custom mb-4">
              {[
                {
                  id: "details",
                  label: "Vehicle Details",
                  icon: "bx bx-detail",
                },
                {
                  id: "money-receipts",
                  label: "Money Receipts",
                  icon: "bx bx-receipt",
                  count: stock?.money_receipts?.length ?? 0,
                },
                {
                  id: "vehicle-movement",
                  label: "Vehicle Movement",
                  icon: "bx bx-map",
                  count: stock?.movements?.length ?? 0,
                },
                {
                  id: "quotations",
                  label: "Quotations",
                  icon: "bx bx-file",
                  count: stock?.quotations?.length ?? 0,
                },
                {
                  id: "pre-orders",
                  label: "Pre-Orders",
                  icon: "bx bx-calendar-check",
                  count: stock?.pre_orders?.length ?? 0,
                },
                {
                  id: "inventory",
                  label: "Inventory",
                  icon: "bx bx-note",
                },
                ...(canViewContents
                  ? [
                      {
                        id: "contents",
                        label: "Contents",
                        icon: "bx bx-share-alt",
                      },
                    ]
                  : []),
                ...(canViewActivity
                  ? [
                      {
                        id: "activity",
                        label: "Activity",
                        icon: "bx bx-history",
                      },
                    ]
                  : []),
              ]?.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={classnames({ active: activeTab === tab.id })}
                    onClick={() => toggleTab(tab.id)}
                  >
                    {tab.icon && <i className={`${tab.icon} me-2`}></i>}
                    {tab.label}
                    {tab.count !== undefined && ` (${tab.count})`}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* Details Tab */}
              <TabPane tabId="details">
                <DetailsTab stock={stock} loading={isLoading} />
              </TabPane>

              {/* Money Receipts Tab */}
              <TabPane tabId="money-receipts">
                <MoneyReceiptsTab stock={stock} onChanged={refetch} />
              </TabPane>

              {/* Vehicle Movement Tab */}
              <TabPane tabId="vehicle-movement">
                <VehicleMovementList vehicleId={stock?.id} vehicle={stock} />
              </TabPane>

              {/* Quotations Tab */}
              <TabPane tabId="quotations">
                <QuotationsTab stock={stock} onChanged={refetch} />
              </TabPane>

              {/* Pre-Orders Tab */}
              <TabPane tabId="pre-orders">
                <PreOrdersTab
                  stock={stock}
                  onChanged={refetch}
                  isRefetching={isFetching}
                />
              </TabPane>
              {/* Inventory Tab */}
              <TabPane tabId="inventory">
                <InventoryTab setInventoryPage={setInventoryPage} />
              </TabPane>

              {canViewContents ? (
                <TabPane tabId="contents">
                  <VehicleContentsTab stock={stock} />
                </TabPane>
              ) : null}

              {canViewActivity ? (
                <TabPane tabId="activity">
                  <ActivityTab stock={stock} />
                </TabPane>
              ) : null}
            </TabContent>
          </Col>
        </Row>
      </Container>

      {/* Document Generation Modal */}
      <DocumentGenerationModal
        docType={docType}
        setDocType={setDocType}
        stock={stock}
      />
    </div>
  )
}

export default StockView
